import React from 'react';
import classNames from 'classnames';
import RenderImg from '@op/components/RenderImg';
import TextClamp from '@opm/components/TextClamp';
import ChannelLabel from '@op/components/ChannelLabel';
import ChannelItem, { normalizeChannelName } from '../ChannelItem';
import CountriesList from '../CountriesList';
import { DBO_TM } from '@op/utils/format';
import { useTranslateConfig } from '@opm/hooks/useTranslateConfig';
import './index.scss';
import PlatformItem from '../PlatformItem';
import BubbleCommon from '@op/components/BubbleCommon';
import useIntls from '@opm/hooks/useIntls';
import PlayableCover from '../PlayableCover';
import { DefaultImg } from '@op/constants/defaultImg';
import SvgIcon from '@home/components/SvgIcon';
import { TOpenWindowsType } from '../type';

// 封面 + 视频时长角标（共用）
export const AdCover = ({
  records,
  open,
  showContent = true,
  className,
}: {
  records: any;
  showContent?: boolean;
  open?: (type?: TOpenWindowsType) => void;
  className?: string;
}) => {
  const { intl } = useIntls();

  return (
    <BubbleCommon
      placement="top"
      tipsMap={null}
      showIcon={false}
      content={showContent ? records.title || intl('No Title') : ''}
    >
      <div className={classNames('dbom-adbox-coverbox', className)} onClick={() => open?.()}>
        <PlayableCover
          previewImg={records.preview_img}
          type={records.type}
          className="dbom-adbox-cover dbom-no-invert"
        />
        {records.video_duration > 0 && (
          <span className="dbom-adbox-duration">
            {DBO_TM.duration(records.video_duration)}
          </span>
        )}
        {/* 鼠标滑过分享icon */}
        {records?.origin_url && (
          <div className="dbom-adbox-cover-share"
            onClick={(e) => {
              e.stopPropagation();
              open?.('ori');
            }}>
            <SvgIcon className="dbom-adbox-cover-share-icon" type="other-share-icon" />
          </div>
        )}
      </div>
    </BubbleCommon>
  );
};

// 头像 + 标题（共用）
const AdHeader = ({ records, open }: { records: any; open?: () => void }) => (
  <div className="dbom-adbox-header" onClick={open}>
    {records.app_logo && (
      <RenderImg
        coverImage={records.app_logo}
        className="dbom-adbox-avatar dbom-no-invert"
        defaultImage={DefaultImg.KOL}
      />
    )}
    <TextClamp
      rowNum={1}
      text={records.app_name || '-'}
      placement="top"
      className="dbom-adbox-title"
    />
  </div>
);

// 日期行（共用）
const AdDateLine = ({ records }: { records: any }) => {
  const { translateType } = useTranslateConfig();
  if (!records.first_seen) return null;
  const adTypeRaw = records.type?.[0];
  const adType = translateType?.(adTypeRaw) || adTypeRaw;

  return (
    <div className="dbom-adbox-des">
      <span className="dbom-adbox-text">
        {adType} <span className="dbom-adbox-separator"></span>{' '}
        {DBO_TM.timeTitle({
          start_time: records.first_seen,
          end_time: records.last_seen,
          date_type: 'daily',
        })}
      </span>
    </div>
  );
};

// Ads 视图：单渠道 icon + 平台文字 + 国家 + 日期
const AdsInfo = ({
  records,
  open,
}: {
  records: any;
  open?: (type?: TOpenWindowsType) => void;
}) => {
  if (!records) return null;
  const channelName = normalizeChannelName(records.channel?.[0] || '');
  const hasCountries = records.countries && records.countries.length > 0;
  const hasPlatform = records.platform && records.platform.length > 0;

  return (
    <div className="dbom-adbox-wrapper">
      <div className="dbom-adbox">
        <AdCover records={records} open={open} />
        <div className="dbom-adbox-infobox">
          <AdHeader records={records} open={() => open?.('game')} />
          <div className="dbom-adbox-source">
            <ChannelLabel
              className={classNames({
                'dbom-line-divider': hasCountries || hasPlatform,
              })}
              showText
              iconStyle={{ width: 16, height: 16 }}
              channel_name={channelName}
              isMarketing
            />
            {hasPlatform && (
              <span
                className={classNames('dbom-adbox-platforms', {
                  'dbom-line-divider': hasCountries,
                })}
              >
                {records.platform.join(', ')}
              </span>
            )}
            <CountriesList countries={records.countries} />
          </div>
          <AdDateLine records={records} />
        </div>
      </div>
    </div>
  );
};

// Creatives 视图：多渠道 icons + 多平台 icons + 国家 + 日期
const CreativesInfo = ({
  records,
  open,
}: {
  records: any;
  open?: (type?: TOpenWindowsType) => void;
}) => {
  if (!records) return null;
  const channels: string[] = records.channel || [];
  const platforms: string[] = records.platform || [];
  const hasCountries = records.countries && records.countries.length > 0;

  return (
    <div className="dbom-adbox-wrapper">
      <div className="dbom-adbox">
        <AdCover records={records} open={open} />
        <div className="dbom-adbox-infobox">
          <AdHeader records={records} open={() => open?.('ori')} />
          <div className="dbom-adbox-source">
            <ChannelItem list={channels} />
            <PlatformItem
              list={platforms}
              className={classNames({ 'dbom-line-divider': hasCountries })}
            />
            <CountriesList countries={records.countries} />
          </div>
          <AdDateLine records={records} />
        </div>
      </div>
    </div>
  );
};

export interface AdInfoCellProps {
  records: any;
  type: string;
  open?: (type?: TOpenWindowsType) => void;
}

const AdInfoCell: React.FC<AdInfoCellProps> = ({ records, type, open }) => {
  if (type === 'ads') {
    return <AdsInfo records={records} open={open} />;
  }
  return <CreativesInfo records={records} open={open} />;
};

export default AdInfoCell;
